/**
 * UGC Videos Section
 * Handles tile click → modal open with embedded video
 */

(function () {
  "use strict";

  // ── Utilities ──────────────────────────────────────────────────────────────

  /**
   * Extract a YouTube video ID from any normal YouTube URL.
   * Supports: watch?v=, youtu.be/, shorts/, embed/
   */
  function getYouTubeId(url) {
    try {
      const u = new URL(url);
      if (u.hostname === "youtu.be") return u.pathname.slice(1).split("?")[0];
      if (u.pathname.includes("/shorts/"))
        return u.pathname.split("/shorts/")[1].split("?")[0];
      if (u.pathname.includes("/embed/"))
        return u.pathname.split("/embed/")[1].split("?")[0];
      return u.searchParams.get("v") || "";
    } catch {
      return "";
    }
  }

  /**
   * Extract a Vimeo video ID from a standard Vimeo URL.
   */
  function getVimeoId(url) {
    try {
      const u = new URL(url);
      // pathname is like /123456789 or /channels/foo/123456789
      const parts = u.pathname.split("/").filter(Boolean);
      // last numeric segment is the video id
      for (let i = parts.length - 1; i >= 0; i--) {
        if (/^\d+$/.test(parts[i])) return parts[i];
      }
      return "";
    } catch {
      return "";
    }
  }

  /**
   * Build the embed HTML to inject into the modal media container.
   */
  function buildEmbedHtml(type, url) {
    if (type === "shopify") {
      return `<video
        src="${url}"
        autoplay
        controls
        playsinline
        style="width:100%;height:100%;object-fit:cover;"
      ></video>`;
    }

    if (type === "youtube") {
      const id = getYouTubeId(url);
      if (!id)
        return '<p style="color:#fff;padding:20px;">Invalid YouTube URL</p>';
      return `<iframe
        src="https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1"
        allow="autoplay; fullscreen; picture-in-picture"
        allowfullscreen
        style="width:100%;height:100%;border:none;"
        title="YouTube video"
      ></iframe>`;
    }

    if (type === "vimeo") {
      const id = getVimeoId(url);
      if (!id)
        return '<p style="color:#fff;padding:20px;">Invalid Vimeo URL</p>';
      return `<iframe
        src="https://player.vimeo.com/video/${id}?autoplay=1&badge=0&autopause=0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowfullscreen
        style="width:100%;height:100%;border:none;"
        title="Vimeo video"
      ></iframe>`;
    }

    return "";
  }

  // ── Initialise each section ────────────────────────────────────────────────

  function initSection(section) {
    const modal = section.querySelector(".ugc-video-modal");
    if (!modal) return;

    const closeBtn = modal.querySelector(".ugc-video-modal__close");
    const prevBtn = modal.querySelector(".ugc-video-modal__nav--prev");
    const nextBtn = modal.querySelector(".ugc-video-modal__nav--next");
    const counterEl = modal.querySelector(".ugc-video-modal__counter-current");
    const totalEl = modal.querySelector(".ugc-video-modal__counter-total");
    const mediaEl = modal.querySelector(".ugc-video-modal__media");

    // Build an ordered list of all video data from the tiles
    const tiles = Array.from(section.querySelectorAll(".ugc-videos__tile"));
    const videoList = tiles.map(function (t) {
      return { type: t.dataset.videoType || "", url: t.dataset.videoUrl || "" };
    });

    if (totalEl) totalEl.textContent = videoList.length;

    let currentIndex = 0;
    let isAnimating = false;
    const ANIM_MS = 320;

    // ── Update arrow disabled state + counter
    function updateNav() {
      if (prevBtn) prevBtn.disabled = currentIndex === 0;
      if (nextBtn) nextBtn.disabled = currentIndex === videoList.length - 1;
      if (counterEl) counterEl.textContent = currentIndex + 1;
    }

    // ── Start playing a <video> element inside a slide, with muted fallback
    function playSlideVideo(slide) {
      const video = slide.querySelector("video");
      if (!video) return;
      video.muted = false;
      video.play().catch(function () {
        video.muted = true;
        video.play();
      });
    }

    // ── Inject a new slide (direction 0 = instant, 1 = next, -1 = prev)
    function loadSlide(index, direction) {
      const { type, url } = videoList[index];
      const html = buildEmbedHtml(type, url);
      if (!html) return;

      const newSlide = document.createElement("div");
      newSlide.className = "ugc-video-modal__slide";
      newSlide.innerHTML = html;

      if (direction === 0) {
        // Initial open — no animation
        mediaEl.innerHTML = "";
        mediaEl.appendChild(newSlide);
        playSlideVideo(newSlide);
        return;
      }

      isAnimating = true;
      const oldSlide = mediaEl.querySelector(".ugc-video-modal__slide");
      const fromX = direction === 1 ? "100%" : "-100%";
      const toX = direction === 1 ? "-100%" : "100%";
      const easing = "cubic-bezier(0.4, 0, 0.2, 1)";
      const trans = `transform ${ANIM_MS}ms ${easing}`;

      // Position the incoming slide off-screen
      newSlide.style.transform = `translateX(${fromX})`;
      mediaEl.appendChild(newSlide);

      // Force reflow so the initial transform is painted before transitioning
      void newSlide.offsetWidth;

      // Slide both panels simultaneously
      newSlide.style.transition = trans;
      newSlide.style.transform = "translateX(0)";

      if (oldSlide) {
        oldSlide.style.transition = trans;
        oldSlide.style.transform = `translateX(${toX})`;
      }

      setTimeout(function () {
        if (oldSlide && oldSlide.parentNode) oldSlide.remove();
        newSlide.style.transition = "";
        isAnimating = false;
        playSlideVideo(newSlide);
      }, ANIM_MS);
    }

    // ── Protect first-tile background autoplay from pauseAllMedia
    function pauseOtherMedia() {
      if (typeof window.pauseAllMedia !== "function") return;
      const bgVideo = section.querySelector(
        ".ugc-videos__tile--autoplay video",
      );
      if (bgVideo) {
        const ph = document.createComment("ugc-ap");
        bgVideo.parentNode.insertBefore(ph, bgVideo);
        bgVideo.parentNode.removeChild(bgVideo);
        window.pauseAllMedia();
        ph.parentNode.insertBefore(bgVideo, ph);
        ph.parentNode.removeChild(ph);
      } else {
        window.pauseAllMedia();
      }
    }

    // ── Open / close
    function openModal(index) {
      currentIndex = index;
      pauseOtherMedia();
      loadSlide(index, 0);
      updateNav();
      modal.setAttribute("open", "");
      document.body.style.overflow = "hidden";
    }

    function closeModal() {
      mediaEl.innerHTML = "";
      modal.removeAttribute("open");
      document.body.style.overflow = "";
      // Resume first-tile background loop
      const bgVideo = section.querySelector(
        ".ugc-videos__tile--autoplay video",
      );
      if (bgVideo) bgVideo.play().catch(function () {});
    }

    // ── Navigate between videos
    function navigate(dir) {
      if (isAnimating) return;
      const newIndex = currentIndex + dir;
      if (newIndex < 0 || newIndex >= videoList.length) return;
      currentIndex = newIndex;
      loadSlide(currentIndex, dir);
      updateNav();
    }

    // ── Tile clicks
    tiles.forEach(function (tile, i) {
      tile.addEventListener("click", function () {
        if (!videoList[i].type || !videoList[i].url) return;
        openModal(i);
      });
    });

    // ── Arrow buttons
    if (prevBtn)
      prevBtn.addEventListener("click", function () {
        navigate(-1);
      });
    if (nextBtn)
      nextBtn.addEventListener("click", function () {
        navigate(1);
      });

    // ── Close button + backdrop
    if (closeBtn) closeBtn.addEventListener("click", closeModal);
    modal.addEventListener("click", function (e) {
      if (e.target === modal) closeModal();
    });

    // ── Keyboard: Escape + arrow keys
    document.addEventListener("keydown", function (e) {
      if (!modal.hasAttribute("open")) return;
      if (e.key === "Escape") {
        closeModal();
        return;
      }
      if (e.key === "ArrowLeft") navigate(-1);
      if (e.key === "ArrowRight") navigate(1);
    });

    // ── Touch swipe on the video frame
    const inner = modal.querySelector(".ugc-video-modal__inner");
    let touchStartX = 0;
    let touchStartY = 0;

    inner.addEventListener(
      "touchstart",
      function (e) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      },
      { passive: true },
    );

    inner.addEventListener(
      "touchend",
      function (e) {
        const dx = e.changedTouches[0].clientX - touchStartX;
        const dy = e.changedTouches[0].clientY - touchStartY;
        // Only trigger if horizontal swipe dominates and exceeds 44px
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 44) {
          navigate(dx < 0 ? 1 : -1);
        }
      },
      { passive: true },
    );
  }

  // ── Boot ───────────────────────────────────────────────────────────────────

  function init() {
    document
      .querySelectorAll(".ugc-videos[data-section-id]")
      .forEach(initSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Re-init if the section is added/reloaded via the theme editor
  document.addEventListener("shopify:section:load", function (e) {
    const section = e.target.querySelector(".ugc-videos[data-section-id]");
    if (section) initSection(section);
  });
})();
